import { useState, useRef, useEffect, useCallback } from 'react';
import JSZip from 'jszip';
import { parseMessage, langColor, RenderedPart } from '../utils/markdownRenderer';
import { useChatHistory, ChatSession, ActionItem } from '../hooks/useChatHistory';
import { useAuth } from '../hooks/useAuth';

import { api, getLocalAgentConfig } from '../api/client';
import { maskSecrets } from '../utils/secret-masker';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

const PRESETS = [
  { icon: '⚡', label: 'Write Function', prompt: 'Write a TypeScript function that ' },
  { icon: '🐛', label: '/fix Debug Code', prompt: '/fix Tìm và sửa lỗi trong đoạn code sau:\n\n```\n\n```' },
  { icon: '🔍', label: '/review Project', prompt: '/review Kiểm tra toàn bộ cấu trúc dự án và đánh giá chất lượng' },
  { icon: '📝', label: '/plan Architecture', prompt: '/plan Thiết kế kiến trúc và lập kế hoạch triển khai cho ' },
];

const SLASH_COMMANDS = [
  { id: 'cmd:plan', name: '/plan', label: '/plan', hint: 'Lập kế hoạch chi tiết trước khi thực thi', icon: '📝', command_intent: 'PLAN' as const },
  { id: 'cmd:fix', name: '/fix', label: '/fix', hint: 'Debug & Sửa lỗi chính xác trong codebase', icon: '🐛', command_intent: 'FIX' as const },
  { id: 'cmd:review', name: '/review', label: '/review', hint: 'Đánh giá, soát và audit cấu trúc mã nguồn', icon: '🔍', command_intent: 'REVIEW' as const },
  { id: 'cmd:test', name: '/test', label: '/test', hint: 'Chạy kiểm thử & thẩm định chất lượng', icon: '🧪', command_intent: 'TEST' as const },
  { id: 'cmd:search', name: '/search', label: '/search', hint: 'Tìm kiếm trên Web và RAG Codebase', icon: '🌐', command_intent: 'SEARCH' as const },
];

function getToken(): string | null { return localStorage.getItem('ericoder_token'); }

// ── Module-level utilities (defined once, no re-creation on render) ──────────

function removeAccents(str: string): string {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();
}

function flexMatch(target: string, queryStr: string): boolean {
  if (!queryStr) return true;
  const rawTarget = target.toLowerCase();
  const normTarget = removeAccents(target);
  const rawQuery = queryStr.toLowerCase().trim();
  const normQuery = removeAccents(queryStr).trim();

  if (rawTarget.includes(rawQuery) || normTarget.includes(normQuery)) return true;

  const tokens = normQuery.split(/\s+/).filter(Boolean);
  if (tokens.length > 0) {
    return tokens.every(tok => normTarget.includes(tok) || rawTarget.includes(tok));
  }

  return false;
}

const DOC_EXTENSIONS = new Set(['md', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv', 'json', 'xml', 'yaml', 'yml', 'rtf', 'odt']);

function getDocIcon(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
  if (['pdf'].includes(ext)) return '📄';
  if (['doc', 'docx'].includes(ext)) return '📑';
  return '📝';
}

// ── Code Block ────────────────────────────────────────────────

function CodeBlock({ code, lang }: { code: string; lang: string }) {
  const [copied, setCopied] = useState(false);
  const [applied, setApplied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadFile = () => {
    const ext: Record<string, string> = {
      typescript: 'ts', javascript: 'js', python: 'py', rust: 'rs',
      go: 'go', sql: 'sql', bash: 'sh', css: 'css', html: 'html', json: 'json',
    };
    const filename = `code.${ext[lang] ?? lang ?? 'txt'}`;
    const blob = new Blob([code], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
  };

  // Detect file path inside code comments if present
  const filePathMatch = code.match(/^(?:\/\/\s*|\/\*\s*|#\s*)(?:filepath:\s*|file:\s*)?([a-zA-Z0-9_\-\.\/\\:]+\.[a-zA-Z0-9]+)/i);
  const detectedPath = filePathMatch ? filePathMatch[1] : '';

  const applyToWorkspace = async () => {
    const targetPath = prompt('Nhập đường dẫn file để áp dụng mã nguồn vào dự án:', detectedPath || 'src/index.ts');
    if (!targetPath) return;

    try {
      const agent = getLocalAgentConfig();
      if (!agent?.tunnel_url) throw new Error("No tunnel URL configured for local agent");
      const url = `${agent.tunnel_url}/execute_tool`;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (agent?.agent_token) headers['Authorization'] = `Bearer ${agent.agent_token}`;

      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          method: 'write_to_file',
          args: { TargetFile: targetPath, CodeContent: code, Overwrite: true }
        })
      });

      if (res.ok) {
        setApplied(true);
        setTimeout(() => setApplied(false), 3000);
      } else {
        alert('Không thể ghi file tới Local Agent. Kiểm tra lại kết nối Eric_MEC_ComputeUse.');
      }
    } catch (e: any) {
      alert(`Lỗi kết nối Local Agent: ${e.message}`);
    }
  };

  return (
    <div style={{
      borderRadius: '10px',
      overflow: 'hidden',
      border: '1px solid rgba(255,255,255,0.08)',
      margin: '8px 0',
      background: '#0d1117',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '6px 12px', background: 'rgba(255,255,255,0.04)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{
            fontSize: '11px', fontWeight: 600, fontFamily: 'var(--font-mono)',
            color: langColor(lang), textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>{lang || 'code'}</span>
          {detectedPath && (
            <span style={{ fontSize: '11px', color: 'var(--accent-cyan)', fontFamily: 'var(--font-mono)' }}>
              📄 {detectedPath}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={applyToWorkspace} style={{ ...btnStyle, color: applied ? '#10b981' : 'var(--accent-cyan)' }} title="Ghi mã nguồn vào file dự án">
            {applied ? '✓ Đã ghi file' : '⚡ Áp dụng vào File'}
          </button>
          <button onClick={downloadFile} style={btnStyle} title="Download file">↓</button>
          <button onClick={copy} style={{ ...btnStyle, color: copied ? '#10b981' : undefined }}>
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
      </div>
      <pre style={{
        margin: 0, padding: '14px 16px', fontSize: '13px', lineHeight: 1.65,
        fontFamily: 'var(--font-mono)', overflowX: 'auto', color: '#e6edf3', whiteSpace: 'pre',
      }}><code>{code}</code></pre>
    </div>
  );
}

const btnStyle: React.CSSProperties = {
  fontSize: '11px', padding: '3px 8px', borderRadius: '5px',
  border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)',
  color: 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'var(--font-mono)',
};

function RenderedPartView({ part }: { part: RenderedPart }) {
  if (part.type === 'code') {
    return <CodeBlock code={part.content} lang={part.language ?? ''} />;
  }

  if (part.type === 'image' && part.url) {
    return (
      <div style={{
        margin: '12px 0',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        background: '#0d1117',
        maxWidth: '540px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
      }}>
        <div style={{
          padding: '8px 12px',
          background: 'rgba(255,255,255,0.04)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '12px',
          color: 'var(--text-secondary)'
        }}>
          <span style={{ fontWeight: 600, color: 'var(--accent-cyan)' }}>🖼️ {part.alt || 'Ảnh AI tạo'}</span>
          <a
            href={part.url}
            target="_blank"
            rel="noreferrer"
            style={{
              color: 'var(--accent-primary)',
              textDecoration: 'none',
              fontSize: '11px',
              padding: '2px 8px',
              borderRadius: '4px',
              background: 'rgba(124, 58, 237, 0.15)',
              border: '1px solid rgba(124, 58, 237, 0.3)'
            }}
          >
            Mở ảnh gốc ↗
          </a>
        </div>
        <div style={{ padding: '8px', display: 'flex', justifyContent: 'center', background: '#000' }}>
          <img
            src={part.url}
            alt={part.alt || 'Generated Image'}
            style={{ maxWidth: '100%', maxHeight: '420px', borderRadius: '8px', objectFit: 'contain', display: 'block' }}
            loading="lazy"
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--text-primary)' }}>
      {part.content}
    </div>
  );
}

// ── Action Timeline ──────────────────────────────────────────

function ActionTimeline({ actions }: { actions?: ActionItem[] }) {
  if (!actions || actions.length === 0) return null;

  const getToolIcon = (tool: string) => {
    switch (tool) {
      case 'list_dir': return '📁';
      case 'read_file': return '📖';
      case 'replace_file_content': return '✏️';
      case 'write_file': return '📝';
      case 'run_command': return '⚡';
      case 'grep_search': return '🔍';
      case 'web_search': return '🌐';
      case 'finish': return '🏁';
      default: return '⚙️';
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      padding: '10px 12px',
      background: 'rgba(15, 23, 42, 0.75)',
      border: '1px solid rgba(148, 163, 184, 0.2)',
      borderRadius: '8px',
      fontSize: '12px',
      maxWidth: '100%',
      backdropFilter: 'blur(8px)',
    }}>
      <div style={{ fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span>🤖 Autonomous Actions</span>
        <span style={{ fontSize: '10px', background: 'rgba(99, 102, 241, 0.2)', color: 'var(--accent-primary)', padding: '1px 6px', borderRadius: '4px' }}>
          {actions.length} {actions.length === 1 ? 'step' : 'steps'}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {actions.map((act, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              padding: '4px 8px',
              borderRadius: '6px',
              background: act.status === 'running' ? 'rgba(99, 102, 241, 0.12)' : 'rgba(255, 255, 255, 0.03)',
              border: act.status === 'running' ? '1px solid rgba(99, 102, 241, 0.3)' : '1px solid transparent',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <span>{getToolIcon(act.tool)}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text-primary)' }}>{act.tool}</span>
              {act.target && (
                <span style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {act.target}
                </span>
              )}
            </div>
            <div style={{ flexShrink: 0 }}>
              {act.status === 'running' && (
                <span style={{ color: 'var(--accent-blue)', fontSize: '11px', fontWeight: 600 }}>⏳ running</span>
              )}
              {act.status === 'success' && (
                <span style={{ color: 'var(--accent-green)', fontSize: '11px', fontWeight: 600 }}>✓ done</span>
              )}
              {act.status === 'error' && (
                <span style={{ color: 'var(--accent-red)', fontSize: '11px', fontWeight: 600 }}>⚠ error</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Session Bubble ────────────────────────────────────────────

function SessionBubble({ session, isPaidUser, activeId }: { session: ChatSession, isPaidUser: boolean, activeId: string | null }) {
  const isRunning = session.state !== 'DONE' && session.state !== 'FAILED';
  const isAwaitingApproval = session.state === 'AWAITING_APPROVAL';
  const isAwaitingOptimization = session.state === 'AWAITING_OPTIMIZATION_APPROVAL';
  const isAnyApproval = isAwaitingApproval || isAwaitingOptimization;
  const [approving, setApproving] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);

  const callAction = async (action: string, extra: Record<string, unknown> = {}) => {
    setApproving(true);
    try {
      const token = localStorage.getItem('ericoder_token');
      const res = await fetch(`/api/swarm/action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ session_id: session.swarm_session_id || activeId, action, ...extra })
      });
      if (!res.ok) {
        const err = await res.text();
        alert(`Lỗi: ${err}`);
      }
    } catch (e: any) {
      alert(`Lỗi kết nối: ${e.message}`);
    } finally {
      setApproving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* USER PROMPT */}
      <div style={{ alignSelf: 'flex-end', maxWidth: '75%', padding: '10px 14px',
        background: 'linear-gradient(135deg, #7c3aed, #06b6d4)',
        borderRadius: '14px 14px 4px 14px', fontSize: '13px', lineHeight: 1.6,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {session.prompt}
      </div>

      {/* SWARM STATUS UI */}
      {session.state !== 'DONE' && (
        <div style={{ alignSelf: 'flex-start', display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '90%' }}>
          
          {isRunning && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
              {!isAnyApproval && <div className="spinner" style={{ width: 14, height: 14 }} />}
              <span style={{ fontWeight: 600 }}>Swarm Agent is active:</span>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent-blue)' }}>{session.state}</span>
              {session.pipelineState && (
                <>
                  <span>→</span>
                  <span style={{ fontFamily: 'var(--font-mono)', color: isAnyApproval ? '#f59e0b' : 'var(--accent-green)' }}>{session.pipelineState}</span>
                </>
              )}
            </div>
          )}

          {/* Action Timeline when actions exist */}
          <ActionTimeline actions={session.actions} />

          {/* ── Approval Panel ─────────────────────────────── */}
          {isAnyApproval && (
            <div style={{
              padding: '16px',
              background: 'rgba(245, 158, 11, 0.08)',
              border: '1px solid rgba(245, 158, 11, 0.4)',
              borderRadius: '10px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <div style={{ fontWeight: 700, color: '#fcd34d', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                ⏸️ {isAwaitingApproval ? 'Đang chờ duyệt kế hoạch / checkpoint' : 'Đang chờ duyệt phương án tối ưu'}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                AI đã hoàn thành bước thiết kế. Vui lòng xem xét và xác nhận để tiếp tục.
              </div>

              {!showFeedback ? (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    disabled={approving}
                    onClick={() => callAction(isAwaitingApproval ? 'APPROVE_PLAN' : 'APPROVE_OPTIMIZATION')}
                    style={{
                      padding: '7px 16px', borderRadius: '7px', border: 'none', cursor: 'pointer',
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      color: '#fff', fontWeight: 700, fontSize: '13px',
                      opacity: approving ? 0.6 : 1
                    }}
                  >
                    {approving ? '...' : '✅ Duyệt & Tiếp tục'}
                  </button>

                  {isAwaitingApproval && (
                    <button
                      disabled={approving}
                      onClick={() => setShowFeedback(true)}
                      style={{
                        padding: '7px 16px', borderRadius: '7px',
                        border: '1px solid rgba(245,158,11,0.5)', cursor: 'pointer',
                        background: 'rgba(245,158,11,0.1)',
                        color: '#fcd34d', fontWeight: 600, fontSize: '13px'
                      }}
                    >
                      ✏️ Yêu cầu chỉnh sửa
                    </button>
                  )}

                  <button
                    disabled={approving}
                    onClick={() => callAction(isAwaitingApproval ? 'REJECT_CHECKPOINT' : 'REJECT_OPTIMIZATION')}
                    style={{
                      padding: '7px 16px', borderRadius: '7px',
                      border: '1px solid rgba(239,68,68,0.4)', cursor: 'pointer',
                      background: 'rgba(239,68,68,0.08)',
                      color: '#f87171', fontWeight: 600, fontSize: '13px',
                      opacity: approving ? 0.6 : 1
                    }}
                  >
                    ✕ Huỷ task
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <textarea
                    value={feedbackText}
                    onChange={e => setFeedbackText(e.target.value)}
                    placeholder="Nhập phản hồi của bạn về kế hoạch này..."
                    rows={3}
                    style={{
                      width: '100%', padding: '8px 10px', borderRadius: '7px',
                      border: '1px solid var(--border)', background: 'var(--bg-glass)',
                      color: 'var(--text-primary)', fontSize: '12px', resize: 'vertical'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      disabled={approving || !feedbackText.trim()}
                      onClick={() => callAction('REVISE_PLAN', { feedback: feedbackText })}
                      style={{
                        padding: '7px 16px', borderRadius: '7px', border: 'none', cursor: 'pointer',
                        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                        color: '#fff', fontWeight: 700, fontSize: '13px',
                        opacity: (approving || !feedbackText.trim()) ? 0.5 : 1
                      }}
                    >
                      {approving ? '...' : '🔄 Gửi & Lập lại kế hoạch'}
                    </button>
                    <button
                      onClick={() => setShowFeedback(false)}
                      style={{
                        padding: '7px 14px', borderRadius: '7px',
                        border: '1px solid var(--border)', cursor: 'pointer',
                        background: 'transparent', color: 'var(--text-secondary)', fontSize: '13px'
                      }}
                    >
                      ← Quay lại
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Healing Warning */}
          {session.state === 'HEALING' && (
            <div style={{ padding: '12px', background: 'rgba(245, 158, 11, 0.1)', borderLeft: '2px solid #f59e0b', borderRadius: '0 8px 8px 0', fontSize: '13px', color: '#fcd34d' }}>
              <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '14px' }}>⚠️</span> Ericoder đang sửa lỗi...
              </div>
            </div>
          )}

        </div>
      )}

      {/* Planner / Reasoning Stream — preserved during processing AND after DONE */}
      {session.planStream && (
        <div style={{ alignSelf: 'flex-start', maxWidth: '90%', width: '90%', padding: '12px', background: 'rgba(16, 185, 129, 0.05)', borderLeft: '2px solid #10b981', borderRadius: '0 8px 8px 0', fontSize: '12px', color: '#a7f3d0' }}>
          <div style={{ fontWeight: 600, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '14px' }}>🧠</span> {session.state === 'DONE' ? 'Ericoder kế hoạch & phân tích:' : 'Ericoder đang phân tích & thiết kế cấu trúc...'}
          </div>
          <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'var(--font-mono)', lineHeight: 1.5, opacity: 0.9 }}>
            {session.planStream}
            {session.state !== 'DONE' && session.state !== 'FAILED' && (
              <span style={{ display: 'inline-block', width: '4px', height: '12px', background: '#10b981', marginLeft: '4px', animation: 'blink 1s step-end infinite' }} />
            )}
          </div>
        </div>
      )}

      {/* Coder Stream — shown during processing AND after DONE */}
      {session.codeStream && (
        <div style={{ alignSelf: 'flex-start', maxWidth: '90%', width: '90%', padding: '12px', background: 'rgba(167, 139, 250, 0.05)', borderLeft: '2px solid #a78bfa', borderRadius: '0 8px 8px 0', fontSize: '13px' }}>
          <div style={{ fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px', color: '#ddd6fe' }}>
            <span style={{ fontSize: '14px' }}>⚡</span> 
            {session.state === 'DONE' ? 'Ericoder phản hồi / mã nguồn:' : 'Ericoder đang soạn thảo mã nguồn...'}
          </div>
          <div>
            {parseMessage(session.codeStream).map((p, i) => (
              <RenderedPartView key={i} part={p} />
            ))}
            {session.state !== 'DONE' && session.state !== 'FAILED' && (
                <span style={{ display: 'inline-block', width: '6px', height: '14px', background: '#a78bfa', marginLeft: '4px', animation: 'blink 1s step-end infinite' }} />
            )}
          </div>
        </div>
      )}

      {/* Sandbox Logs */}
      {session.sandboxLogs && (
        <div style={{ alignSelf: 'flex-start', maxWidth: '90%', width: '90%', padding: '12px', background: '#0d1117', borderLeft: '2px solid #ef4444', borderRadius: '0 8px 8px 0', fontSize: '12px', color: '#e6edf3', fontFamily: 'var(--font-mono)' }}>
          <div style={{ fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px', color: '#ef4444' }}>
            <span style={{ fontSize: '14px' }}>🔥</span> Ericoder đang chạy thử nghiệm...
          </div>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowX: 'auto', maxHeight: '300px' }}>
            {session.sandboxLogs}
          </pre>
        </div>
      )}

      {/* FINAL RESULT (shown if available and not identical to codeStream) */}
      {session.state === 'DONE' && session.finalResult && session.finalResult.trim() !== (session.codeStream || '').trim() && (
        <div style={{ alignSelf: 'flex-start', maxWidth: '90%', width: '90%' }}>
          {parseMessage(session.finalResult).map((p, i) => (
            <RenderedPartView key={i} part={p} />
          ))}
        </div>
      )}
      
      {session.error && (
        <div style={{ alignSelf: 'flex-start', padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', color: '#f87171', fontSize: '12px' }}>
          ⚠ {(!isPaidUser && session.error.includes('Stream disconnected')) ? 'Hãy nâng cấp tài khoản trả phí để sử dụng tính năng này' : session.error}
        </div>
      )}
    </div>
  );
}

async function extractTextFromPdf(file: File): Promise<string> {
  return new Promise(async (resolve, reject) => {
    try {
      if (!(window as any).pdfjsLib) {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.min.js';
        document.head.appendChild(script);
        
        await new Promise((res) => {
          script.onload = res;
        });
      }
      
      const pdfjsLib = (window as any).pdfjsLib;
      pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js';
      
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const typedarray = new Uint8Array(e.target?.result as ArrayBuffer);
          const pdf = await pdfjsLib.getDocument({ data: typedarray }).promise;
          let text = '';
          
          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const content = await page.getTextContent();
            const strings = content.items.map((item: any) => item.str);
            text += strings.join(' ') + '\n';
          }
          
          resolve(text.trim());
        } catch (err) {
          reject(err);
        }
      };
      
      reader.onerror = (err) => reject(err);
      reader.readAsArrayBuffer(file);
    } catch (err) {
      reject(err);
    }
  });
}

function forwardStreamEventToAgent(event: {
  type: string;
  chunk?: string;
  agent?: string;
  logs?: string;
  step?: string;
  session_id: string;
}) {
  try {
    const agent = getLocalAgentConfig();
    const targets: { url: string; token?: string }[] = [];
    if (agent?.tunnel_url) {
      targets.push({ url: agent.tunnel_url, token: agent.agent_token });
    }
    targets.push({ url: 'http://127.0.0.1:8080', token: agent?.agent_token });
    targets.push({ url: 'http://localhost:8080', token: agent?.agent_token });

    for (const target of targets) {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (target.token) headers['Authorization'] = `Bearer ${target.token}`;
      fetch(`${target.url}/stream_event`, {
        method: 'POST',
        headers,
        body: JSON.stringify(event)
      }).catch(() => {});
    }
  } catch (_) {}
}

// ── Chat Page ─────────────────────────────────────────────────

export function ChatPage() {
  const { user } = useAuth();
  const isPaidUser = user?.role === 'admin' || (user as any)?.is_paid;
  const [input, setInput]       = useState('');
  const [projectRules, setProjectRules] = useState('');
  const [loading, setLoading]   = useState(false);
  const [attachment, setAttachment] = useState<{ file: File; url: string; uploading: boolean; type?: string; name?: string; content?: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'chat' | 'preview'>('chat');
  const [previewUrl, setPreviewUrl] = useState('http://localhost:5173');
  const endRef    = useRef<HTMLDivElement>(null);
  const inputRef  = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);


  const {
    conversations,
    activeId,
    activeProjectId,
    projects,
    localWorkspaces,
    createConversation,
    updateMessages
  } = useChatHistory();

  const activeConv = conversations.find(c => c.id === activeId);
  const currentMessages = activeConv?.messages ?? [];

  // ── Mentions & Slash Command Autocomplete State ─────────────────────────────
  const [projectFiles, setProjectFiles] = useState<{ name: string; path: string; type: 'file' | 'folder' }[]>([]);
  const [autocompleteQuery, setAutocompleteQuery] = useState('');
  const [autocompleteType, setAutocompleteType] = useState<'mention' | 'slash' | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Load initial prompt if pushed from EricMEC / Pipeline
  useEffect(() => {
    const initPrompt = sessionStorage.getItem('ericoder_initial_prompt');
    if (initPrompt) {
      sessionStorage.removeItem('ericoder_initial_prompt');
      setInput(initPrompt);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
    }
  }, [activeId]);

  // Load project files and folders list when active conversation or project changes
  useEffect(() => {
    let isMounted = true;
    const loadFiles = async () => {
      const currentProjId = activeConv?.projectId || activeProjectId || (localWorkspaces && localWorkspaces[0]) || (projects && projects[0]?.id) || 'default';
      
      let targetWorkspacePath = '';
      if (currentProjId !== 'default') {
        const foundLocal = (localWorkspaces || []).find((ws: string) => 
          ws === currentProjId || 
          ws.endsWith('\\' + currentProjId) || 
          ws.endsWith('/' + currentProjId) ||
          ws.split(/[\\/]/).pop() === currentProjId
        );
        targetWorkspacePath = foundLocal || currentProjId;
      }

      const filesList: { name: string; path: string; type: 'file' | 'folder' }[] = [];

      // 1. Gateway R2 Project Tree
      try {
        const data = await api.getProjectTree(currentProjId);
        const flatten = (items: any[]) => {
          for (const item of items || []) {
            filesList.push({ name: item.name, path: item.path, type: item.type === 'directory' ? 'folder' : 'file' });
            if (item.children) flatten(item.children);
          }
        };
        flatten(data.items ?? []);
      } catch (_) {}

      // 2. Local Agent execution bridge (Port 8080)
      try {
        const agent = getLocalAgentConfig();
        if (!agent?.tunnel_url) throw new Error("No tunnel URL");
        const url = `${agent.tunnel_url}/execute_tool`;
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (agent?.agent_token) headers['Authorization'] = `Bearer ${agent.agent_token}`;

        const res = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify({ method: 'getProjectTree', args: { path: targetWorkspacePath } })
        });
        if (res.ok) {
          const localData = await res.json();
          const items = localData.data?.items || localData.items || [];
          const flattenLocal = (treeItems: any[]) => {
            for (const item of treeItems || []) {
              const itemType = (item.type === 'directory' || item.type === 'folder') ? 'folder' : 'file';
              if (!filesList.some(f => f.path === item.path)) {
                filesList.push({ name: item.name, path: item.path, type: itemType });
              }
              if (item.children) flattenLocal(item.children);
            }
          };
          flattenLocal(items);
        }
      } catch (_) {}

      if (isMounted) {
        setProjectFiles(filesList);
      }
    };

    loadFiles();
    return () => { isMounted = false; };
  }, [activeConv?.projectId, activeProjectId, projects, localWorkspaces]);

  // Filtered autocomplete items (Slash commands or Context mentions)
  const query = autocompleteQuery;

  const DOC_EXTENSIONS_SET = DOC_EXTENSIONS; // Module-level constant (defined at top of file)

  // Category 1: Plans, Tasks & Workspace Documents
  const docWorkspaceFiles = projectFiles.filter(f => {
    if (f.type !== 'file') return false;
    const ext = f.name.split('.').pop()?.toLowerCase() || '';
    return DOC_EXTENSIONS_SET.has(ext);
  });

  const builtInItems = [
    { id: 'plan:impl', type: 'plan' as const, category: 'Plan', label: 'Implementation Plan', path: 'implementation_plan.md', icon: '📝' },
    { id: 'plan:walkthrough', type: 'plan' as const, category: 'Plan', label: 'Walkthrough', path: 'walkthrough.md', icon: '📝' },
    { id: 'task:active', type: 'task' as const, category: 'Task', label: 'Swarm Task Plan', path: 'task.md', icon: '📋' },
    ...docWorkspaceFiles.map(f => ({
      id: `doc:${f.path}`,
      type: 'file' as const,
      category: 'Doc',
      label: f.name,
      path: f.path,
      icon: getDocIcon(f.name)
    }))
  ].filter(i => flexMatch(i.label, query) || flexMatch(i.path, query));


  // Category 2: Media / Attachments
  const mediaItems = attachment ? [
    { id: 'media:attachment', type: 'media' as const, category: 'Media', label: `Media (${attachment.name || 'File'})`, path: attachment.name || 'attached_file', icon: '🖼️' }
  ].filter(i => flexMatch(i.label, query)) : [];

  // Category 3: Folders & Code Files
  const filteredFolders = projectFiles.filter(f => f.type === 'folder' && (flexMatch(f.name, query) || flexMatch(f.path, query))).slice(0, 5);
  const filteredFiles = projectFiles.filter(f => {
    if (f.type !== 'file') return false;
    const ext = f.name.split('.').pop()?.toLowerCase() || '';
    return !DOC_EXTENSIONS.has(ext) && (flexMatch(f.name, query) || flexMatch(f.path, query));
  }).slice(0, 10);

  // Category 4: Past Conversations
  const filteredChats = conversations.filter(c => c.id !== activeId && flexMatch(c.title, query)).slice(0, 5);

  // Category 5: Slash Commands
  const filteredSlashCommands = SLASH_COMMANDS.filter(cmd =>
    flexMatch(cmd.name, query) || flexMatch(cmd.hint, query)
  );

  const allAutocompleteItems = autocompleteType === 'slash'
    ? filteredSlashCommands.map(cmd => ({
        id: cmd.id,
        type: 'slash' as const,
        category: 'Slash Command',
        label: cmd.name,
        path: cmd.hint,
        icon: cmd.icon,
      }))
    : [
        ...builtInItems,
        ...mediaItems,
        ...filteredFolders.map(f => ({ id: `folder:${f.path}`, type: 'folder' as const, category: 'Folder', label: f.name, path: f.path, icon: '📁' })),
        ...filteredFiles.map(f => ({ id: `file:${f.path}`, type: 'file' as const, category: 'File', label: f.name, path: f.path, icon: '📄' })),
        ...filteredChats.map(c => ({ id: `chat:${c.id}`, type: 'chat' as const, category: 'Chat', label: c.title, path: undefined as string | undefined, icon: '💬' }))
      ];

  const selectAutocompleteItem = (item: any) => {
    if (!inputRef.current) return;
    const textarea = inputRef.current;
    const cursor = textarea.selectionStart;
    const textBeforeCursor = input.slice(0, cursor);
    const textAfterCursor = input.slice(cursor);

    if (autocompleteType === 'slash') {
      const lastSlashPos = textBeforeCursor.lastIndexOf('/');
      if (lastSlashPos !== -1) {
        const commandText = `${item.label} `;
        const newText = input.slice(0, lastSlashPos) + commandText + textAfterCursor;
        setInput(newText);
        setAutocompleteType(null);
        setTimeout(() => {
          textarea.focus();
          const newPos = lastSlashPos + commandText.length;
          textarea.setSelectionRange(newPos, newPos);
        }, 0);
      }
    } else {
      const lastAtPos = textBeforeCursor.lastIndexOf('@');
      if (lastAtPos !== -1) {
        let mentionText = `@${item.label} `;
        if (item.type === 'file' || item.type === 'folder') {
          mentionText = `@${item.path} `;
        } else if (item.type === 'chat') {
          mentionText = `@"${item.label}" `;
        }

        const newText = input.slice(0, lastAtPos) + mentionText + textAfterCursor;
        setInput(newText);
        setAutocompleteType(null);

        setTimeout(() => {
          textarea.focus();
          const newPos = lastAtPos + mentionText.length;
          textarea.setSelectionRange(newPos, newPos);
        }, 0);
      }
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInput(value);
    autoHeight(e.target);

    const cursor = e.target.selectionStart;
    const textBeforeCursor = value.slice(0, cursor);

    // 1. Check Slash Command trigger: starts at index 0 or after whitespace/newline
    const lastSlash = textBeforeCursor.lastIndexOf('/');
    if (lastSlash !== -1) {
      const charBefore = lastSlash > 0 ? textBeforeCursor[lastSlash - 1] : ' ';
      const queryCandidate = textBeforeCursor.slice(lastSlash + 1);
      if ((charBefore === ' ' || charBefore === '\n' || lastSlash === 0) && !queryCandidate.includes(' ') && !queryCandidate.includes('\n')) {
        setAutocompleteQuery(queryCandidate);
        setAutocompleteType('slash');
        setSelectedIndex(0);
        return;
      }
    }

    // 2. Check Mention trigger: starts after whitespace/newline or at 0
    const lastAt = textBeforeCursor.lastIndexOf('@');
    if (lastAt !== -1) {
      const charBefore = lastAt > 0 ? textBeforeCursor[lastAt - 1] : ' ';
      const queryCandidate = textBeforeCursor.slice(lastAt + 1);
      if ((charBefore === ' ' || charBefore === '\n' || lastAt === 0) && !queryCandidate.includes('\n')) {
        setAutocompleteQuery(queryCandidate);
        setAutocompleteType('mention');
        setSelectedIndex(0);
        return;
      }
    }

    setAutocompleteType(null);
  };

  // Keep a ref so SSE reconnect effect always reads fresh conversations without re-running
  const conversationsRef = useRef(conversations);
  useEffect(() => { conversationsRef.current = conversations; }, [conversations]);

  const activeStreamReaderRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const activeSwarmSessionIdRef = useRef<string | null>(null);

  // Sync project rules with active conversation
  useEffect(() => {
    if (activeConv) {
      setProjectRules(activeConv.projectRules || '');
    } else {
      setProjectRules('');
    }
  }, [activeId, activeConv]);

  // Auto scroll to bottom
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [currentMessages]);

  const stopCurrentTask = useCallback(async () => {
    try {
      if (activeStreamReaderRef.current) {
        await activeStreamReaderRef.current.cancel().catch(() => {});
        activeStreamReaderRef.current = null;
      }
      const activeSessionId = activeSwarmSessionIdRef.current;
      if (activeSessionId) {
        const token = getToken();
        fetch(`${API_BASE}/api/swarm/action`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ session_id: activeSessionId, action: 'CANCEL_TASK' })
        }).catch(() => {});
      }
    } finally {
      setLoading(false);
      activeSwarmSessionIdRef.current = null;
      if (activeConv) {
        updateMessages(activeConv.id, activeConv.messages.map(s =>
          (s.state !== 'DONE' && s.state !== 'FAILED')
            ? { ...s, state: 'FAILED', error: '🛑 Đã dừng bởi người dùng (Cancelled)' }
            : s
        ), projectRules);
      }
    }
  }, [activeConv, projectRules, updateMessages]);

  // ── SSE Reconnect after page refresh ──────────────────────────────────────
  // If the active conversation has an in-progress session with a known swarm_session_id,
  // reconnect the SSE stream so live state updates keep flowing.
  useEffect(() => {
    if (!activeConv) return;
    // Find a running message that has a swarm_session_id saved
    const runningMsg = activeConv.messages.find(
      m => m.swarm_session_id && m.state !== 'DONE' && m.state !== 'FAILED'
    );
    if (!runningMsg?.swarm_session_id) return;

    const swarmId = runningMsg.swarm_session_id;
    const activeConvId = activeConv.id; // capture stable ID, not stale object
    const token = localStorage.getItem('ericoder_token');
    let cancelled = false;

    const connectSSE = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/stream?session_id=${swarmId}`, {
          method: 'GET',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok || cancelled) return;
        if (!res.body) return; // Guard: body may be absent (e.g. 204 No Content)

        const reader = res.body!.getReader();
        const dec = new TextDecoder();
        let buf = '';

        while (!cancelled) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const lines = buf.split('\n');
          buf = lines.pop() ?? '';
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const payload = JSON.parse(line.slice(6).trim());
              if (payload.type === 'state') {
                const conv = conversationsRef.current.find(c => c.id === activeConvId);
                if (!conv) { cancelled = true; break; }
                updateMessages(activeConvId, conv.messages.map(s =>
                  s.swarm_session_id === swarmId
                    ? { ...s, state: payload.step, finalResult: (payload.result !== undefined && payload.result !== null && payload.result !== '') ? payload.result : s.finalResult, error: payload.error ?? s.error }
                    : s
                ));
                forwardStreamEventToAgent({ type: 'state', step: payload.step, session_id: swarmId });
                if (payload.step === 'DONE' || payload.step === 'FAILED') { cancelled = true; break; }
              } else if (payload.type === 'pipeline_state') {
                const conv = conversationsRef.current.find(c => c.id === activeConvId);
                if (!conv) { cancelled = true; break; }
                updateMessages(activeConvId, conv.messages.map(s =>
                  s.swarm_session_id === swarmId ? { ...s, pipelineState: payload.state } : s
                ));
              } else if (payload.type === 'stream_chunk') {
                const conv = conversationsRef.current.find(c => c.id === activeConvId);
                if (!conv) { cancelled = true; break; }
                updateMessages(activeConvId, conv.messages.map(s => {
                  if (s.swarm_session_id !== swarmId) return s;
                  if (payload.agent === 'planner') return { ...s, planStream: (s.planStream || '') + payload.chunk };
                  return { ...s, codeStream: (s.codeStream || '') + payload.chunk };
                }));
                forwardStreamEventToAgent({
                  type: 'stream_chunk',
                  agent: payload.agent,
                  chunk: payload.chunk,
                  session_id: swarmId
                });
              } else if (payload.type === 'action_start') {
                const conv = conversationsRef.current.find(c => c.id === activeConvId);
                if (!conv) { cancelled = true; break; }
                updateMessages(activeConvId, conv.messages.map(s => {
                  if (s.swarm_session_id !== swarmId) return s;
                  const curActions = s.actions || [];
                  const newAction: ActionItem = {
                    tool: payload.tool,
                    target: payload.target || '',
                    status: 'running',
                    iteration: payload.iteration,
                  };
                  return { ...s, actions: [...curActions, newAction] };
                }));
              } else if (payload.type === 'action_end') {
                const conv = conversationsRef.current.find(c => c.id === activeConvId);
                if (!conv) { cancelled = true; break; }
                updateMessages(activeConvId, conv.messages.map(s => {
                  if (s.swarm_session_id !== swarmId) return s;
                  const curActions = s.actions || [];
                  const updated = [...curActions];
                  let found = false;
                  for (let i = updated.length - 1; i >= 0; i--) {
                    if (updated[i].tool === payload.tool && updated[i].status === 'running') {
                      updated[i] = { ...updated[i], status: payload.status, target: payload.target || updated[i].target };
                      found = true;
                      break;
                    }
                  }
                  if (!found) {
                    updated.push({
                      tool: payload.tool,
                      target: payload.target || '',
                      status: payload.status,
                      iteration: payload.iteration,
                    });
                  }
                  return { ...s, actions: updated };
                }));
              }
            } catch { /* ignore parse errors */ }
          }
        }
      } catch (e) {
        console.warn('[SSE Reconnect] failed:', e);
      }
    };

    connectSSE();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const autoHeight = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 240) + 'px';
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    
    // 1. Limit Check for multiple direct files
    if (files.length > 10) {
      if (window.confirm('Dự án của bạn quá lớn để phân tích trực tiếp (Vượt quá 10 files). Bạn có muốn đẩy toàn bộ lên EricMEC Drive để đánh chỉ mục RAG nhằm tiết kiệm bộ nhớ AI không?')) {
        alert('Vui lòng vào màn hình Projects (Dự án) và dùng tính năng Upload để đưa dự án này vào EricMEC Drive.');
      }
      return;
    }

    // 2. Handle ZIP extraction
    if (files.length === 1 && files[0].name.toLowerCase().endsWith('.zip')) {
      const file = files[0];
      setAttachment({ file, url: '', uploading: true });
      try {
        const zip = new JSZip();
        const content = await zip.loadAsync(file);
        let validFilesCount = 0;
        let concatenatedContent = '';

        for (const [relativePath, zipEntry] of Object.entries(content.files)) {
          if (zipEntry.dir) continue;
          if (relativePath.includes('node_modules/') || relativePath.includes('.git/') || relativePath.includes('dist/')) continue;
          
          validFilesCount++;
          if (validFilesCount > 10) {
            alert('File ZIP chứa hơn 10 files mã nguồn. Vui lòng upload trực tiếp lên EricMEC Drive để đánh chỉ mục RAG.');
            setAttachment(null);
            return;
          }
          const text = await zipEntry.async('text');
          concatenatedContent += `\n\n[File: ${relativePath}]\n${text}\n[/File]`;
        }

        setAttachment({
          file,
          url: '',
          uploading: false,
          type: 'document',
          name: `${file.name} (${validFilesCount} files)`,
          content: concatenatedContent
        });
      } catch (err: any) {
        alert(`Không thể giải nén file ZIP: ${err.message}`);
        setAttachment(null);
      }
      return;
    }

    // 3. Handle multiple direct code files
    if (files.length > 1) {
       setAttachment({ file: files[0], url: '', uploading: true });
       try {
         let concatenatedContent = '';
         for (const f of files) {
           const text = await f.text();
           concatenatedContent += `\n\n[File: ${f.name}]\n${text}\n[/File]`;
         }
         setAttachment({
           file: files[0],
           url: '',
           uploading: false,
           type: 'document',
           name: `${files.length} files attached`,
           content: concatenatedContent
         });
       } catch (err: any) {
         setAttachment(null);
       }
       return;
    }

    // 4. Fallback Single File logic (Images, PDFs, etc.)
    const file = files[0];
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const isImage = file.type.startsWith('image/');
    const isDoc = ['txt', 'md', 'docx', 'doc', 'pdf', 'ts', 'tsx', 'js', 'jsx', 'json'].includes(ext) || file.type.startsWith('text/') || file.type === 'application/pdf';
    
    if (!isImage && !isDoc) {
      alert('Định dạng file không được hỗ trợ.');
      return;
    }
    
    setAttachment({ file, url: '', uploading: true });
    try {
      let content = '';
      if (ext === 'pdf' || file.type === 'application/pdf') {
        try {
          content = await extractTextFromPdf(file);
        } catch (e: any) {
          console.error('Failed to parse PDF client-side:', e);
        }
      } else if (isDoc && ext !== 'pdf') {
        content = await file.text();
      }
      
      let url = '';
      let type = isImage ? 'image' : 'document';
      if (isImage) {
         const res = await api.uploadImage(file);
         url = res.url;
         type = res.type || 'image';
      }
      
      setAttachment({ 
        file, 
        url, 
        uploading: false,
        type,
        name: file.name,
        content: content
      });
    } catch (err: any) {
      alert(`Upload failed: ${err.message}`);
      setAttachment(null);
    }
  };


  const send = useCallback(async (text?: string) => {
    const prompt = (text ?? input).trim();
    if (!prompt || loading) return;
    
    setInput('');
    setLoading(true);
    const sentAttachment = attachment;
    setAttachment(null);
    if (inputRef.current) { inputRef.current.style.height = '44px'; }

    // 1. Get or create conversation ID
    let currentSessionId = activeId;
    let currentConv = activeConv;
    
    if (!currentSessionId) {
      const targetProjectId = activeConv?.projectId || activeProjectId || (localWorkspaces && localWorkspaces[0]) || (projects && projects[0]?.id) || 'default';
      currentSessionId = createConversation(targetProjectId, prompt);
      currentConv = {
        id: currentSessionId,
        title: prompt.length > 30 ? prompt.substring(0, 30) + '...' : prompt,
        projectId: targetProjectId,
        projectRules: projectRules,
        messages: [],
        created_at: new Date().toISOString()
      };
    }

    const token = getToken();
    const tempId = `temp_${Date.now()}`;
    const initialMessages: ChatSession[] = [
      ...(currentConv?.messages ?? []),
      {
        id: tempId,
        prompt,
        state: 'STARTING',
        pipelineState: '',
        planStream: '',
        codeStream: '',
        finalResult: '',
      }
    ];

    // Update messages locally in localStorage
    updateMessages(currentSessionId!, initialMessages, projectRules);

    let finalSessionId = currentSessionId!;
    
    let promptWithDoc = prompt;
    if (sentAttachment && sentAttachment.content && (sentAttachment.type === 'document' || sentAttachment.type === 'pdf')) {
      const documentPrompt = `\n\n[Đính kèm file: ${sentAttachment.name}]\n${sentAttachment.content}\n[Hết nội dung file: ${sentAttachment.name}]`;
      promptWithDoc = prompt + documentPrompt;
    }

    // ── Resolve @file_name & @chat_name Mentions ──────────────────────────────
    const currentProjId = currentConv?.projectId || activeProjectId || (localWorkspaces && localWorkspaces[0]) || (projects && projects[0]?.id) || 'default';

    let targetWorkspacePath = '';
    if (currentProjId !== 'default') {
      const foundLocal = (localWorkspaces || []).find((ws: string) => 
        ws === currentProjId || 
        ws.endsWith('\\' + currentProjId) || 
        ws.endsWith('/' + currentProjId) ||
        ws.split(/[\\/]/).pop() === currentProjId
      );
      targetWorkspacePath = foundLocal || currentProjId;
    }

    // 1. Resolve @file_name references
    const fileMatches = Array.from(prompt.matchAll(/@([a-zA-Z0-9_\-\.\/]+)/g));
    for (const match of fileMatches) {
      const targetPath = match[1];
      const matchedFile = projectFiles.find(f => f.path === targetPath || f.name === targetPath || f.path.endsWith('/' + targetPath));
      if (matchedFile) {
        let content = '';
        // Try Local Agent (Port 8080)
        try {
          const agent = getLocalAgentConfig();
          if (!agent?.tunnel_url) throw new Error("No tunnel URL");
          const url = `${agent.tunnel_url}/execute_tool`;
          const headers: Record<string, string> = { 'Content-Type': 'application/json' };
          if (agent?.agent_token) headers['Authorization'] = `Bearer ${agent.agent_token}`;

          let fetchPath = matchedFile.path;
          if (targetWorkspacePath && !fetchPath.startsWith(targetWorkspacePath)) {
            const sep = targetWorkspacePath.includes('\\') ? '\\' : '/';
            fetchPath = `${targetWorkspacePath}${sep}${matchedFile.path}`;
          }

          const res = await fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify({ method: 'getFileContent', args: { path: fetchPath } })
          });
          if (res.ok) {
            const fileRes = await res.json();
            content = fileRes.data?.content || fileRes.content || '';
          }
        } catch (_) {}

        // Fallback to Gateway R2 API
        if (!content) {
          try {
            const fileRes = await api.getFileContent(currentProjId, matchedFile.path);
            content = fileRes.content || '';
          } catch (_) {}
        }

        if (content) {
          promptWithDoc += `\n\n[Nội dung file tham chiếu: @${matchedFile.path}]\n${content}\n[Hết nội dung file: @${matchedFile.path}]`;
        }
      }
    }

    // 2. Resolve @"Chat Title" and @Plan/@Task references
    // Use looser regex: @"any title" OR @word (then flex-match against known items)
    const chatRefRegex = /@"([^"\n]+)"|@(\S[^\s@]*)/g;
    const chatMatches = Array.from(prompt.matchAll(chatRefRegex));
    const resolvedChatIds = new Set<string>();
    for (const match of chatMatches) {
      const targetTitle = (match[1] || match[2] || '').trim();
      if (!targetTitle) continue;
      // Find by exact title match, then flexMatch fallback
      const matchedConv = conversations.find(c =>
        c.id !== currentSessionId &&
        !resolvedChatIds.has(c.id) &&
        (c.title === targetTitle || flexMatch(c.title, targetTitle))
      );
      if (matchedConv) {
        resolvedChatIds.add(matchedConv.id);
        const historySummary = matchedConv.messages
          .map(m => `User: ${m.prompt}\nAI Result: ${m.finalResult || m.codeStream || '(In Progress)'}`)
          .join('\n---\n');
        promptWithDoc += `\n\n[Đoạn chat tham chiếu: @"${matchedConv.title}"]\n${historySummary}\n[Hết đoạn chat tham chiếu]`;
      }
    }

    // 3. Inject active workspace context for review, question or analysis prompts
    if (currentProjId && currentProjId !== 'default' && projectFiles.length > 0) {
      const isProjectQuery = /\b(review|đánh giá|kiểm tra|tổng quan|giới thiệu|phân tích|cấu trúc|dự án|project|codebase|app|ứng dụng|file|hệ thống|toàn bộ)\b/i.test(prompt);
      if (isProjectQuery && !promptWithDoc.includes('[Cấu trúc files dự án]')) {
        const topFiles = projectFiles.slice(0, 60).map(f => f.path).join('\n');
        const projName = currentProjId.split(/[\\/]/).pop() || currentProjId;
        promptWithDoc = `[Dự án đang kết nối: ${projName} (Đường dẫn: ${currentProjId})]\n[Cấu trúc files dự án]:\n${topFiles}\n[Hết cấu trúc files]\n\n` + promptWithDoc;
      }
    }

    // 4. Extract slash command intent & assemble editor_context
    let commandIntent: 'PLAN' | 'FIX' | 'REVIEW' | 'TEST' | 'SEARCH' | 'DEFAULT' = 'DEFAULT';
    const trimmedPrompt = prompt.trim();
    if (trimmedPrompt.startsWith('/plan')) commandIntent = 'PLAN';
    else if (trimmedPrompt.startsWith('/fix') || trimmedPrompt.startsWith('/debug')) commandIntent = 'FIX';
    else if (trimmedPrompt.startsWith('/review') || trimmedPrompt.startsWith('/audit')) commandIntent = 'REVIEW';
    else if (trimmedPrompt.startsWith('/test') || trimmedPrompt.startsWith('/qa')) commandIntent = 'TEST';
    else if (trimmedPrompt.startsWith('/search')) commandIntent = 'SEARCH';

    const editorContext = {
      command_intent: commandIntent,
      active_workspace: currentProjId,
    };

    try {
      // 2. Call POST /api/swarm to init task
      const executionMode = localStorage.getItem('ericoder_execution_mode') || 'cloud';
      const promptToSend = executionMode === 'local' ? maskSecrets(promptWithDoc).maskedText : promptWithDoc;

      const res = await fetch(`${API_BASE}/api/swarm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          prompt: promptToSend,
          project_rules: projectRules,
          project_id: currentProjId,
          user_id: 'web_ui',
          editor_context: editorContext,
          execution_mode: executionMode,
          mode: executionMode,
          attachments: sentAttachment && sentAttachment.type === 'image' ? [{ url: sentAttachment.url, type: 'image_url' }] : undefined,
        }),
      });


      if (!res.ok) {
        if (res.status === 429) {
          throw new Error('Hãy nâng cấp tài khoản trả phí để sử dụng tính năng này');
        }
        const err = await res.json().catch(() => ({}));
        const errorMsg = typeof err.error === 'string' ? err.error : (err.error?.message ?? err.message ?? `HTTP ${res.status}`);
        throw new Error(errorMsg);
      }

      const data = await res.json();
      const actualSessionId = data.session_id;

      // Update ID in UI — also persist swarm_session_id so actions survive page refresh
      let updatedMessages = initialMessages.map(s => s.id === tempId ? { ...s, state: 'INIT', swarm_session_id: actualSessionId } : s);
      updateMessages(finalSessionId, updatedMessages, projectRules);

      // 3. Connect SSE to /api/stream
      const streamRes = await fetch(`${API_BASE}/api/stream?session_id=${actualSessionId}`, {
        method: 'GET',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        }
      });

      if (!streamRes.ok) {
        throw new Error(`Stream connection failed: ${streamRes.status}`);
      }

      const reader = streamRes.body!.getReader();
      activeStreamReaderRef.current = reader;
      activeSwarmSessionIdRef.current = actualSessionId;
      const dec = new TextDecoder();
      let buffer = '';

      const localUpdate = (updater: (s: ChatSession) => ChatSession) => {
        updatedMessages = updatedMessages.map(s => s.id === tempId ? updater(s) : s);
        updateMessages(finalSessionId, updatedMessages, projectRules);
      };

      const processStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              setLoading(false);
              break;
            }
            
            buffer += dec.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';

            let shouldBreak = false;
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const dataStr = line.slice(6).trim();
                if (!dataStr) continue;
                
                try {
                  const payload = JSON.parse(dataStr);
                  if (payload.type === 'state') {
                    if (payload.step === 'HEALING') {
                      localUpdate(s => ({
                        ...s,
                        state: payload.step,
                        healingMessage: payload.message,
                        reviewerFeedback: payload.feedback || s.reviewerFeedback,
                        // Keep codeStream so user can see the code that was reviewed before healing
                        sandboxLogs: undefined 
                      }));
                    } else {
                      localUpdate(s => ({ 
                        ...s, 
                        state: payload.step, 
                        finalResult: (payload.result !== undefined && payload.result !== null && payload.result !== '') ? payload.result : s.finalResult,
                        error: payload.error ?? s.error,
                        // Preserve codeStream and planStream (don't clear on state transitions)
                      }));
                      if (payload.step === 'DONE' || payload.step === 'FAILED') {
                        setLoading(false);
                        shouldBreak = true;
                      }
                    }
                    forwardStreamEventToAgent({ type: 'state', step: payload.step, session_id: actualSessionId });
                  } 
                  else if (payload.type === 'sandbox_logs') {
                    localUpdate(s => ({ ...s, sandboxLogs: payload.logs }));
                    forwardStreamEventToAgent({ type: 'sandbox_logs', logs: payload.logs, session_id: actualSessionId });
                  }
                  else if (payload.type === 'pipeline_state') {
                    localUpdate(s => ({ ...s, pipelineState: payload.state }));
                  }
                  else if (payload.type === 'stream_chunk') {
                    localUpdate(s => {
                      if (payload.agent === 'planner') return { ...s, planStream: (s.planStream || '') + payload.chunk };
                      return { ...s, codeStream: (s.codeStream || '') + payload.chunk };
                    });
                    forwardStreamEventToAgent({ 
                      type: 'stream_chunk', 
                      agent: payload.agent, 
                      chunk: payload.chunk,
                      session_id: actualSessionId
                    });
                  }
                  else if (payload.type === 'action_start') {
                    localUpdate(s => {
                      const curActions = s.actions || [];
                      const newAction: ActionItem = {
                        tool: payload.tool,
                        target: payload.target || '',
                        status: 'running',
                        iteration: payload.iteration,
                      };
                      return { ...s, actions: [...curActions, newAction] };
                    });
                  }
                  else if (payload.type === 'action_end') {
                    localUpdate(s => {
                      const curActions = s.actions || [];
                      const updated = [...curActions];
                      let found = false;
                      for (let i = updated.length - 1; i >= 0; i--) {
                        if (updated[i].tool === payload.tool && updated[i].status === 'running') {
                          updated[i] = { ...updated[i], status: payload.status, target: payload.target || updated[i].target };
                          found = true;
                          break;
                        }
                      }
                      if (!found) {
                        updated.push({
                          tool: payload.tool,
                          target: payload.target || '',
                          status: payload.status,
                          iteration: payload.iteration,
                        });
                      }
                      return { ...s, actions: updated };
                    });
                  }
                  else if (payload.type === 'local_tool_request') {
                    const { request_id, method, args } = payload;
                    // Run async to avoid blocking the stream reader
                    (async () => {
                      try {
                        const agent = getLocalAgentConfig();
                        if (!agent?.tunnel_url) throw new Error("No tunnel URL configured");
                        const url = `${agent.tunnel_url}/execute_tool`;
                        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
                        if (agent?.agent_token) headers['Authorization'] = `Bearer ${agent.agent_token}`;

                        const localRes = await fetch(url, {
                          method: 'POST',
                          headers,
                          body: JSON.stringify({ method, args })
                        });
                        const result = await localRes.json();
                        await fetch(`${API_BASE}/api/swarm/action`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                          },
                          body: JSON.stringify({
                            action: 'LOCAL_TOOL_RESULT',
                            session_id: actualSessionId,
                            request_id,
                            result
                          })
                        });
                      } catch (err: any) {
                        console.error('Failed to execute local tool:', err);
                        await fetch(`${API_BASE}/api/swarm/action`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                          },
                          body: JSON.stringify({
                            action: 'LOCAL_TOOL_RESULT',
                            session_id: actualSessionId,
                            request_id,
                            result: { stdout: '', stderr: `Local Agent connection error: ${err.message}`, exitCode: 1 }
                          })
                        });
                      }
                    })();
                  }
                  // BUG-2 FIX: Browser nhận yêu cầu zip+upload workspace khi session local kết thúc
                  else if (payload.type === 'local_upload_request') {
                    const { upload_id, session_id } = payload;
                    (async () => {
                      const isSovereign = (localStorage.getItem('ericoder_execution_mode') || 'cloud') === 'local';
                      const allowCloudSync = localStorage.getItem('ericoder_allow_cloud_sync') === 'true';

                      if (isSovereign && !allowCloudSync) {
                        console.log('[Local Sovereign] Workspace cloud upload skipped — 100% sovereign privacy mode active.');
                        await fetch(`${API_BASE}/api/swarm/action`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                          },
                          body: JSON.stringify({
                            action: 'LOCAL_UPLOAD_RESULT',
                            session_id: actualSessionId,
                            upload_id,
                            ok: true,
                          })
                        }).catch(() => {});
                        return;
                      }

                      let uploadOk = false;
                      let uploadError: string | undefined;
                      try {
                        const agent = getLocalAgentConfig();
                        if (!agent?.tunnel_url) throw new Error("No tunnel URL configured");
                        const url = `${agent.tunnel_url}/zip_workspace`;
                        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
                        if (agent?.agent_token) headers['Authorization'] = `Bearer ${agent.agent_token}`;

                        // 1. Yêu cầu local agent zip toàn bộ workspace → trả về binary
                        const zipRes = await fetch(url, {
                          method: 'POST',
                          headers,
                          body: JSON.stringify({ session_id })
                        });
                        if (!zipRes.ok) throw new Error(`Local agent zip failed: ${zipRes.status}`);

                        // 2. Stream ZIP binary lên Gateway (POST /internal/workspace/{sessionId})
                        const uploadRes = await fetch(`${API_BASE}/internal/workspace/${session_id}`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/zip',
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                          },
                          body: zipRes.body,
                          // @ts-ignore — duplex required for streaming body in some environments
                          duplex: 'half',
                        });
                        if (!uploadRes.ok) throw new Error(`Gateway upload failed: ${uploadRes.status}`);
                        uploadOk = true;
                        console.log(`[Local Upload] Workspace uploaded for session ${session_id}`);
                      } catch (err: any) {
                        uploadError = err.message;
                        console.error('[Local Upload] Failed:', err);
                      }

                      // 3. Báo kết quả về Gateway để resolve pending promise
                      await fetch(`${API_BASE}/api/swarm/action`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        },
                        body: JSON.stringify({
                          action: 'LOCAL_UPLOAD_RESULT',
                          session_id: actualSessionId,
                          upload_id,
                          ok: uploadOk,
                          error: uploadError,
                        })
                      }).catch(e => console.error('[Local Upload] Failed to report result:', e));
                    })();
                  }

                } catch (e) {
                  console.error("SSE parse error", e, dataStr);
                }
              }
            }
            if (shouldBreak) break;
          }
        } catch (e) {
          console.error("Stream error", e);
          localUpdate(s => ({ ...s, state: 'FAILED', error: 'Stream disconnected' }));
          setLoading(false);
        }
      };

      processStream();

    } catch (e) {
      updateMessages(finalSessionId, initialMessages.map(s => {
        if (s.id === tempId) {
          return { ...s, state: 'FAILED', error: String(e) };
        }
        return s;
      }), projectRules);
      setLoading(false);
    }
  }, [input, loading, activeId, activeConv, projects, projectRules, createConversation, updateMessages]);

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (autocompleteType && allAutocompleteItems.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % allAutocompleteItems.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + allAutocompleteItems.length) % allAutocompleteItems.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        if (allAutocompleteItems[selectedIndex]) {
          selectAutocompleteItem(allAutocompleteItems[selectedIndex]);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setAutocompleteType(null);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - var(--header-h) - 48px)', gap: 0 }}>

      {/* ── Header ─── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 0 16px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          <div>
            <h1 className="page-title">{activeConv ? activeConv.title : 'Ericoder Swarm UI'}</h1>
            <p className="page-subtitle">Auto-routing AI Agents with Speculative Pre-warming</p>
          </div>
          <div style={{ display: 'flex', background: 'var(--bg-glass)', borderRadius: '8px', padding: '4px', border: '1px solid var(--border)' }}>
            <button
              onClick={() => setActiveTab('chat')}
              style={{
                padding: '6px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                background: activeTab === 'chat' ? 'var(--accent-primary)' : 'transparent',
                color: activeTab === 'chat' ? '#fff' : 'var(--text-secondary)',
                transition: 'all 0.2s ease'
              }}
            >
              💬 Chat Manager
            </button>
            <button
              onClick={() => setActiveTab('preview')}
              style={{
                padding: '6px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                background: activeTab === 'preview' ? 'var(--accent-primary)' : 'transparent',
                color: activeTab === 'preview' ? '#fff' : 'var(--text-secondary)',
                transition: 'all 0.2s ease',
                display: 'flex', alignItems: 'center', gap: '6px'
              }}
            >
              👀 UX/UI Preview
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <div className="badge badge--emerald" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} />
            Swarm Engine Online
          </div>
          {currentMessages.length > 0 && (
            <button className="btn btn--secondary btn--sm" onClick={() => {
              const targetProjectId = activeConv?.projectId || activeProjectId || (localWorkspaces && localWorkspaces[0]) || (projects && projects[0]?.id) || 'default';
              createConversation(targetProjectId);
            }}>
              + New Chat
            </button>
          )}
        </div>
      </div>

      {/* Chat View Tab (Always mounted, hidden via CSS when inactive) */}
      <div style={{ display: activeTab === 'chat' ? 'flex' : 'none', flex: 1, flexDirection: 'column', minHeight: 0 }}>
        {/* ── Messages ─── */}
        <div style={{
          flex: 1, overflowY: 'auto', minHeight: 0,
          display: 'flex', flexDirection: 'column', gap: '24px',
          padding: '16px',
          background: 'var(--bg-glass)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          marginBottom: '12px',
        }}>
        {currentMessages.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '24px' }}>
            <div style={{ fontSize: '48px', opacity: 0.2 }}>{'⬡'}</div>
            <div style={{ fontSize: '15px', color: 'var(--text-secondary)', fontWeight: 600, textAlign: 'center' }}>
              Ask anything. The Swarm will automatically classify <br/>and route to the best agents.
            </div>
            {/* Quick presets */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', maxWidth: '600px' }}>
              {PRESETS.map(p => (
                <button key={p.label} onClick={() => { setInput(p.prompt); inputRef.current?.focus(); }} style={{
                  padding: '8px 14px', borderRadius: '999px', fontSize: '12px', cursor: 'pointer',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-elevated)',
                  color: 'var(--text-secondary)',
                  display: 'flex', alignItems: 'center', gap: '6px',
                  transition: 'all var(--transition)',
                }}>
                  <span>{p.icon}</span>{p.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {currentMessages.map(s => <SessionBubble key={s.id} session={s} isPaidUser={isPaidUser} activeId={activeId} />)}

        <div ref={endRef} />
      </div>

      {/* ── Input ─── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0 }}>
        <input 
          type="text" 
          value={projectRules} 
          onChange={e => setProjectRules(e.target.value)} 
          placeholder="Optional: Project Rules (e.g. Use strict types, No ORMs)"
          style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-glass)', color: 'var(--text-primary)', fontSize: '12px' }}
        />
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, position: 'relative' }}>

            {/* ── Mention / Slash Autocomplete Popup Overlay ── */}
            {autocompleteType && allAutocompleteItems.length > 0 && (
              <div style={{
                position: 'absolute',
                bottom: 'calc(100% + 8px)',
                left: 0,
                right: 0,
                maxHeight: '300px',
                overflowY: 'auto',
                background: 'rgba(18, 18, 38, 0.96)',
                backdropFilter: 'blur(16px)',
                border: '1px solid var(--border-accent)',
                borderRadius: '12px',
                boxShadow: '0 12px 36px rgba(0,0,0,0.8), 0 0 20px rgba(124,58,237,0.3)',
                zIndex: 100,
                padding: '6px'
              }}>
                <div style={{ padding: '6px 10px', fontSize: '11px', fontWeight: 700, color: 'var(--accent-cyan)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>{autocompleteType === 'slash' ? '⚡ Slash Commands (/plan, /fix, /review, /test)' : '💡 Context Mentions (@Task, @Plan, @File, @Chat)'}</span>
                  <span style={{ fontSize: '10px', opacity: 0.7 }}>↑↓ Navigate • Enter Select</span>
                </div>
                {allAutocompleteItems.map((item, idx) => {
                  let mainLabel = item.label;
                  let dirPath = item.path || '';
                  if (dirPath && dirPath.includes('/')) {
                    const lastSlash = dirPath.lastIndexOf('/');
                    dirPath = dirPath.slice(0, lastSlash);
                  } else if (dirPath && dirPath.includes('\\')) {
                    const lastSlash = dirPath.lastIndexOf('\\');
                    dirPath = dirPath.slice(0, lastSlash);
                  } else if (dirPath === mainLabel) {
                    dirPath = '';
                  }

                  return (
                    <div
                      key={item.id}
                      onClick={() => selectAutocompleteItem(item)}
                      onMouseEnter={() => setSelectedIndex(idx)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px',
                        fontSize: '13px',
                        background: idx === selectedIndex ? 'rgba(124, 58, 237, 0.25)' : 'transparent',
                        borderLeft: idx === selectedIndex ? '3px solid var(--accent-cyan)' : '3px solid transparent',
                        color: idx === selectedIndex ? '#ffffff' : 'var(--text-primary)',
                        transition: 'all 0.12s ease'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', overflow: 'hidden' }}>
                        <span style={{ fontSize: '15px', flexShrink: 0 }}>{item.icon}</span>
                        <span style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{mainLabel}</span>
                      </div>
                      {dirPath && (
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '45%' }}>
                          {dirPath}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {attachment && (
              <div style={{ position: 'absolute', top: '-40px', left: '10px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '8px', padding: '4px', display: 'flex', alignItems: 'center', gap: '8px', zIndex: 10 }}>
                {attachment.uploading ? (
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Uploading...</span>
                ) : (
                  <>
                    {attachment.type === 'image' ? (
                      <img src={attachment.url} alt="attached" style={{ height: '24px', width: '24px', objectFit: 'cover', borderRadius: '4px' }} />
                    ) : (
                      <span style={{ fontSize: '12px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        📄 {attachment.name}
                      </span>
                    )}
                    <button onClick={() => setAttachment(null)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '14px' }}>✕</button>
                  </>
                )}
              </div>
            )}
            <input 
              type="file" 
              ref={fileInputRef} 
              style={{ display: 'none' }} 
              accept="image/*,.pdf,.txt,.md,.docx,.doc,.zip,.ts,.tsx,.js,.jsx,.json" 
              multiple
              onChange={handleFileSelect} 
            />
            <button 
              onClick={() => fileInputRef.current?.click()}
              title="Attach File"

              style={{
                position: 'absolute', left: '10px', bottom: '10px', background: 'none', border: 'none',
                color: 'var(--text-secondary)', cursor: 'pointer', zIndex: 10,
                opacity: 0.7, padding: '4px'
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>
            </button>
            <textarea
              ref={inputRef}
              className="textarea"
              value={input}
              onChange={handleTextareaChange}
              onKeyDown={handleKey}
              placeholder={loading ? 'Swarm is processing... (Click Stop to cancel)' : 'Type @ to search files or past chats... (Enter to send)'}
              disabled={loading}
              rows={1}
              style={{
                width: '100%', resize: 'none', minHeight: '44px', maxHeight: '240px',
                fontSize: '13px', lineHeight: 1.5, padding: '12px 50px 12px 40px',
                fontFamily: 'var(--font-sans)', boxSizing: 'border-box',
              }}
            />
          </div>
          {loading ? (
            <button
              className="btn btn--danger"
              onClick={stopCurrentTask}
              style={{
                height: '44px', minWidth: '80px', justifyContent: 'center', flexShrink: 0, borderRadius: '10px',
                background: '#ef4444', color: '#fff', border: 'none', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px',
                cursor: 'pointer'
              }}
              title="Dừng xử lý tác vụ hiện tại"
            >
              <span>🛑 Stop</span>
            </button>
          ) : (
            <button
              className="btn btn--primary"
              onClick={() => send()}
              disabled={!input.trim()}
              style={{ height: '44px', minWidth: '72px', justifyContent: 'center', flexShrink: 0, borderRadius: '10px' }}
            >
              <span>Send ↵</span>
            </button>
          )}
        </div>
      </div>
      </div>

      {/* UX/UI Live Preview Tab (Always mounted, hidden via CSS when inactive) */}
      <div style={{ display: activeTab === 'preview' ? 'flex' : 'none', flex: 1, flexDirection: 'column', gap: '12px', minHeight: 0, marginTop: '12px' }}>
        <div style={{ display: 'flex', gap: '8px', padding: '8px', background: 'var(--bg-glass)', border: '1px solid var(--border)', borderRadius: '8px' }}>
          <input 
            type="text" 
            value={previewUrl}
            onChange={e => setPreviewUrl(e.target.value)}
            placeholder="http://localhost:5173"
            style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '13px', fontFamily: 'var(--font-mono)' }}
          />
          <button 
            onClick={() => {
              const iframe = document.getElementById('preview-iframe') as HTMLIFrameElement;
              if (iframe) iframe.src = iframe.src;
            }}
            style={{ padding: '0 16px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600 }}
          >
            🔄 Tải lại
          </button>
          <a 
            href={previewUrl}
            target="_blank"
            rel="noreferrer"
            style={{ display: 'flex', alignItems: 'center', padding: '0 16px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', textDecoration: 'none', fontWeight: 600 }}
          >
            Mở Tab mới ↗
          </a>
        </div>
        <div style={{ flex: 1, borderRadius: '8px', border: '1px solid var(--border)', background: '#fff', overflow: 'hidden' }}>
          {previewUrl ? (
            <iframe 
              id="preview-iframe"
              src={previewUrl} 
              style={{ width: '100%', height: '100%', border: 'none', backgroundColor: '#fff' }}
              title="Live UI Preview"
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#666', background: '#f5f5f5' }}>
              Vui lòng nhập địa chỉ Dev Server (VD: http://localhost:5173)
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
